import React, { useState } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { Box, Typography, Grid } from '@mui/material';
import { parseToPtBrMonth } from '../../service/date.service';

const CalendarPicker = ({
  handleDateClick,
  selectedDate,
  minDate = dayjs(),
}) => {
  const minDateObj = minDate
    ? dayjs.isDayjs(minDate)
      ? minDate
      : dayjs(minDate)
    : dayjs();

  const selectedDateObj = selectedDate
    ? dayjs.isDayjs(selectedDate)
      ? selectedDate
      : dayjs(selectedDate)
    : null;

  const [date, setDate] = useState(selectedDateObj || minDateObj);

  dayjs.locale('pt-br');

  const renderCalendar = () => {
    const monthStart = date.startOf('month');
    const monthEnd = date.endOf('month');
    const startDate = monthStart.startOf('week');
    const endDate = monthEnd.endOf('week');
    const days = [];
    let day = startDate;

    while (day.isBefore(endDate)) {
      days.push(day);
      day = day.add(1, 'day');
    }

    return days.map((day) => {
      const isPastDate = day.isBefore(minDate, 'day');
      const isSameDate = day.isSame(selectedDate, 'day');
      const isToday = day.isSame(dayjs(), 'day');
      const canSelectDate = !isPastDate && !isToday;

      const onClick = () => {
        if (canSelectDate) {
          handleDateClick(day, true);
        }
      };

      return (
        <Box
          key={day.toISOString()}
          onClick={onClick}
          sx={{
            width: '100%',
            height: '36px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: canSelectDate ? 'pointer' : 'not-allowed',

            backgroundColor: !canSelectDate ? 'grey.50' : 'transparent',
            '&:hover': {
              backgroundColor: canSelectDate ? 'primary.light' : 'grey.50',
            },
          }}
        >
          <Typography
            variant="body2"
            sx={{
              minWidth: '3ch',
              textAlign: 'center',
              display: 'inline-block',
              color:
                isSameDate || (isToday && !selectedDate)
                  ? 'primary.main'
                  : isPastDate
                  ? 'grey.100'
                  : 'text.secondary',
            }}
          >
            {day.format('D')}
          </Typography>
        </Box>
      );
    });
  };

  const handlePreviousMonth = (event) => {
    event.stopPropagation(); // Add this
    setDate((prevDate) => {
      const newDate = prevDate.subtract(1, 'month');
      return newDate;
    });
  };

  const handleNextMonth = (event) => {
    event.stopPropagation(); // Add this
    setDate((prevDate) => {
      const newDate = prevDate.add(1, 'month');
      return newDate;
    });
  };

  return (
    <Box
      display={'flex'}
      flexDirection={'column'}
      alignItems={'center'}
      justifyContent={'center'}
      sx={{
        width: '100%',
        padding: '12px',
        borderRadius: '8px',
        boxShadow: 3,
        backgroundColor: 'background.paper',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '12px',
          width: '100%',
        }}
      >
        <Typography
          onClick={handlePreviousMonth}
          sx={{
            cursor: 'pointer',
            fontWeight: 'bold',
            color: 'primary.main',
          }}
        >
          &lt;
        </Typography>
        <Typography variant="h6" sx={{ fontSize: '16px' }}>
          {parseToPtBrMonth(date)} / {date.format('YYYY')}
        </Typography>
        <Typography
          onClick={handleNextMonth}
          sx={{
            cursor: 'pointer',
            fontWeight: 'bold',
            color: 'primary.main',
          }}
        >
          &gt;
        </Typography>
      </Box>

      {/* Days of the Week */}
      <Box
        sx={{
          marginBottom: '8px',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
        }}
      >
        {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map((day) => (
          <Typography
            key={day}
            variant="body2"
            align="center"
            sx={{
              fontWeight: 'bold',
              color: 'text.secondary',
              fontSize: '14px',
              flex: 1,
              textAlign: 'center',
            }}
          >
            {day}
          </Typography>
        ))}
      </Box>

      {/* Days */}
      <Grid container>
        {renderCalendar().map((dayBox, index) => (
          <Grid key={index} sx={{ width: 'calc(100% / 7)' }}>
            {dayBox}
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default CalendarPicker;
